import { lazy, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { withRouter, ScreenOverlay, Spinner } from "../../components/Common";
import { useJob, useJobComplete, useJobDelete, useJobStatusUpdate } from "../../common/hooks/api/job";
import { toastError, toastSuccess } from "../../helpers/toast-helper";
import { useQueryClient } from "@tanstack/react-query";
import { getResponseItemData } from "../../helpers/response-helper";
import useAuthUser from "../../common/hooks/authUser";
import { Card, CardBody } from "reactstrap";

const JobDetails = lazy(() => import("./components/details"));

const JobDetailsPage = () => {
    const { id } = useParams();
    const history = useNavigate();
    const { isAdmin, user } = useAuthUser();
    const queryClient = useQueryClient();
    const { data: jobResponse, isLoading, queryKey: jobQueryKey } = useJob(id, {
        onError: () => {
            toastError("Cannot fetch the job");
        },
    });
    const { mutate: updateJobStatus, isPending: isSaving } = useJobStatusUpdate();
    const { mutate: deleteJob, isPending: isDeleting } = useJobDelete();
    const { mutate: completeJob, isPending: isCompleting } = useJobComplete();

    const { data } = getResponseItemData(jobResponse);
    const onStatusChanged = (jobStatusId) => {
        updateJobStatus({
            jobId: +id!,
            jobStatusId,
        }, {
            onSuccess: () => {
                toastSuccess("Job status was successfully updated");
                queryClient.invalidateQueries({ queryKey: jobQueryKey });
            },
            onError: () => {
                toastError("Cannot update job status");
            },
        });
    };

    const onCompleteClicked = () => {
        completeJob(+id!, {
            onSuccess: () => {
                toastSuccess("Job was successfully marked as complete");
                queryClient.invalidateQueries({ queryKey: jobQueryKey });
            },
            onError: () => {
                toastError("Cannot complete the job");
            },
        });
    };

    const onDeleteClicked = () => {
        deleteJob(id, {
            onSuccess: async () => {
                await toastSuccess("Job was successfully deleted");
                history('/jobs');
            },
            onError: () => { toastError("Cannot delete the job"); }
        });
    };

    const isAdminOrOwner = isAdmin || (data?.userId === user.id);

    if (!isLoading && !isAdminOrOwner) {
        return (
            <Card className="mt-4">
                <CardBody>
                    You don&apos;t have permissions to see this job details
                </CardBody>
            </Card>
        );
    }

    return (
        <>
            {!isLoading && (
                <Suspense>
                    <JobDetails
                        data={data}
                        isSaving={isSaving}
                        isDeleting={isDeleting}
                        isCompleting={isCompleting}
                        onStatusChanged={onStatusChanged}
                        onCompleteClicked={onCompleteClicked}
                        onDeleteClicked={onDeleteClicked}
                    />
                </Suspense>
            )}

            {isLoading && (
                <>
                    <ScreenOverlay />
                    <Spinner />
                </>
            )}
        </>
    );
};

export default withRouter(JobDetailsPage);
